import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  WritableSignal,
  effect,
  inject,
  signal,
} from '@angular/core';

import { PendingRequestsService } from '../../service/pending-requests.service';

const SHOW_DELAY_MS = 300;
const MIN_VISIBLE_MS = 400;

@Component({
  selector: 'app-request-progress',
  template: `
    @if (isVisible()) {
      <div
        aria-hidden="true"
        class="fixed inset-x-0 top-0 z-50 h-0.5 overflow-hidden bg-primary/15"
      >
        <div class="app-request-progress h-full w-1/3 bg-primary"></div>
      </div>
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RequestProgressComponent {
  protected readonly isVisible: WritableSignal<boolean> = signal(false);

  private readonly pendingRequests = inject(PendingRequestsService);
  private readonly destroyRef = inject(DestroyRef);
  private timer: ReturnType<typeof setTimeout> | undefined;
  private shownAt = 0;

  constructor() {
    this.destroyRef.onDestroy((): void => this.clearTimer());

    effect((): void => {
      if (this.pendingRequests.isPending()) {
        this.scheduleShow();

        return;
      }

      this.scheduleHide();
    });
  }

  private scheduleShow(): void {
    if (this.isVisible()) {
      return;
    }

    this.clearTimer();
    this.timer = setTimeout((): void => {
      this.shownAt = Date.now();
      this.isVisible.set(true);
    }, SHOW_DELAY_MS);
  }

  private scheduleHide(): void {
    this.clearTimer();

    if (!this.isVisible()) {
      return;
    }

    const shownFor = Date.now() - this.shownAt;
    const remaining = Math.max(0, MIN_VISIBLE_MS - shownFor);

    this.timer = setTimeout((): void => this.isVisible.set(false), remaining);
  }

  private clearTimer(): void {
    if (this.timer !== undefined) {
      clearTimeout(this.timer);
      this.timer = undefined;
    }
  }
}
